/**
 * Number Formatting Utilities
 *
 * Consistent number formatting across all pages
 */

/**
 * Format number with Thai units (พันล้าน, ล้าน, พัน) and comma separators
 *
 * @param value - Number or string to format
 * @returns Formatted string with Thai units
 *
 * @example
 * formatNumber(7409376646) // "7.41 พันล้าน"
 * formatNumber(1500000) // "1.50 ล้าน"
 * formatNumber(3500) // "3.50 พัน"
 * formatNumber(999) // "999"
 */
export function formatNumber(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(num)) {
    return String(value)
  }

  // Billions (พันล้าน)
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(2)} พันล้าน`
  }

  // Millions (ล้าน)
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)} ล้าน`
  }

  // Thousands (พัน)
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(2)} พัน`
  }

  // Less than 1000 - show with comma separator
  return num.toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * Format number with full comma separators (no unit abbreviation)
 *
 * @param value - Number or string to format
 * @returns Formatted string with commas
 *
 * @example
 * formatNumberFull(7409376646) // "7,409,376,646"
 * formatNumberFull(1500000) // "1,500,000"
 */
export function formatNumberFull(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(num)) {
    return String(value)
  }

  return num.toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * Format percentage
 *
 * @param value - Number or string to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercent(91.01) // "91.0%"
 * formatPercent(91.01, 2) // "91.01%"
 */
export function formatPercent(value: string | number, decimals: number = 1): string {
  const num = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(num)) {
    return '0%'
  }

  return `${num.toFixed(decimals)}%`
}
